// docs/54 LinkStore: the live connection's health once the session has reached the World. The SDK
// (2.13, `withAutomaticReconnect`, forced by the Provider's ConnectionManager) keeps one
// `DbConnection` across a dropped socket and replays every subscription when it is back, so a drop
// is not a new session: the client stays on its screen, holds input (RC2), and catches up from the
// replayed rows. `reconnecting` is the grace window (RC1); past `GRACE_MS` it becomes `lost`, the
// blocking card with Reconnect now (RC3). The boot path (never connected) stays `session.phase`.
import { create } from 'zustand';
import { deviceNow } from '../time';

export type LinkState = 'online' | 'reconnecting' | 'lost';

/** RC1: how long a drop shows only the reconnecting strip before the blocking card. */
export const GRACE_MS = 15_000;
/** RC4: a drop shorter than this comes back without a "back in the World" toast. */
export const QUIET_BLIP_MS = 5_000;

export interface LinkStore {
  state: LinkState;
  /** device ms the link dropped (undefined while online) */
  since?: number;
  /** the SDK's next reconnect attempt (1-based) and when it fires */
  attempt: number;
  nextAttemptAt?: number;
  /** `navigator.onLine` is false: the device itself has no network */
  deviceOffline: boolean;
  reason?: string;
  /** the SDK will not retry this connection (protocol error, ended); Reconnect now builds a new one */
  ended: boolean;
  /** bumped by Reconnect now when a new connection has to be built (main.tsx remounts the Provider) */
  rebuilds: number;
  dropped(reason: string, next?: { attempt?: number; delayMs?: number }): void;
  restored(): void;
  setDeviceOffline(offline: boolean): void;
  rebuild(): void;
}

let graceTimer: ReturnType<typeof setTimeout> | undefined;

export const useLink = create<LinkStore>()((set, get) => ({
  state: 'online',
  attempt: 0,
  deviceOffline: typeof navigator !== 'undefined' && navigator.onLine === false,
  ended: false,
  rebuilds: 0,
  dropped(reason, next) {
    const s = get();
    const since = s.state === 'online' ? deviceNow() : s.since;
    set({
      state: s.state === 'lost' ? 'lost' : 'reconnecting',
      since, reason,
      attempt: next?.attempt ?? s.attempt + 1,
      nextAttemptAt: next?.delayMs !== undefined ? deviceNow() + next.delayMs : undefined,
      ended: next?.attempt === undefined,
    });
    if (s.state === 'online') {
      if (graceTimer) clearTimeout(graceTimer);
      graceTimer = setTimeout(() => { if (get().state === 'reconnecting') set({ state: 'lost' }); }, GRACE_MS);
    }
  },
  restored() {
    if (graceTimer) { clearTimeout(graceTimer); graceTimer = undefined; }
    set({ state: 'online', since: undefined, attempt: 0, nextAttemptAt: undefined, reason: undefined, ended: false });
  },
  setDeviceOffline(offline) { set({ deviceOffline: offline }); },
  rebuild() { set({ rebuilds: get().rebuilds + 1, ended: false }); },
}));

/** Whether a reducer call can go out now (RC2). */
export const linkUp = (): boolean => useLink.getState().state === 'online' && !useLink.getState().deviceOffline;
export const useLinkUp = (): boolean => useLink((s) => s.state === 'online' && !s.deviceOffline);
/** RC2: the tooltip on a start button held while offline. */
export const OFFLINE_TITLE = 'Offline: this waits for the connection to come back';
